import _ from "lodash"
import PropTypes from "prop-types"
import React, { useCallback, useEffect, useRef, useState } from "react"
import { toast } from "react-semantic-toasts"
import {
  Button,
  Divider,
  Dropdown,
  Form,
  Grid,
  Header,
  Icon,
  Label,
  Modal,
  Popup,
  Search,
  Segment,
} from "semantic-ui-react"
import IconButton from "../../../components/shared/buttons/IconButton"
import PaginatedReusableTable from "../../../components/shared/PaginatedReusableTable"
import { history } from "../../../routers/AppRouters"
import constants from "../../../utils/constants"
import { amISuperAdmin } from "../../../utils/functionUtils"
import "./ResearchComponent.css"

const INITIAL_FILTERS = {
  nameSurname: "",
  office: null,
  company: null,
  internalLevel: null,
  language: null,
  manager: null,
  unit: null, // Not exposed in UI but required by the BE in the query params
  softSkill: null,
  technicalSkill: null,
  certification: null,
  practice: null,
  customer: null,
  role: null,
  sector: null,
}

/**
 * Filter labels mapping for the active filter chips display.
 */
const FILTER_LABELS = {
  certification: "Certification",
  role: "Business Title",
  sector: "Sector",
  softSkill: "Soft Skill",
  technicalSkill: "Technical Skill",
  internalLevel: "Internal Level",
  office: "Office Location",
  company: "Company",
  manager: "Manager",
  customer: "Customer",
  language: "Language",
  practice: "Practice",
}

const DEBOUNCE_DELAY = 400

/**
 * Research section of the CV Generator module.
 * Allows searching consultants by name/surname and applying advanced filters.
 *
 * Props:
 *   auth                      {Object}    Auth state with abilities array
 *   warningPopupClicked       {boolean}   Whether warning popup has been shown
 *   updatePopupClicked        {Function}  Callback to mark warning popup as shown
 *   officesOptions            {Array}     Dropdown options for office locations
 *   companiesOptions          {Array}     Dropdown options for companies
 *   internalLevelsOptions     {Array}     Dropdown options for internal levels
 *   languagesOptions          {Array}     Dropdown options for languages
 *   managerOptions            {Array}     Dropdown options for managers
 *   roleOptions               {Array}     Dropdown options for roles
 *   sectorOptions             {Array}     Dropdown options for sectors
 *   softSkillsOptions         {Array}     Dropdown options for soft skills
 *   technicalSkillsOptions    {Array}     Dropdown options for technical skills
 *   certificationsOptions     {Array}     Dropdown options for certifications
 *   categoriesOptions         {Array}     Dropdown options for technical skill categories
 *   customerOptions           {Array}     Dropdown options for customers
 *   researchRaw               {Array}     Search results from the API
 *   loading                   {boolean}   Loading state for the search operation
 *   getResearch               {Function}  Thunk to search consultants. Signature: fn(filtersId)
 *   createDocs                {Function}  Thunk to generate CV document. Signature: fn(uniqueId, language, onSuccess, onFailure)
 *   language                  {string}    Current language setting
 *   reloadSoftSkills          {Function}  Callback to reload soft skills options
 *   getTechnicalSkills        {Function}  Callback to reload technical skills by category. Signature: fn(categoryId)
 *   getLanguages              {Function}  Callback to reload languages. Signature: fn(language)
 */
const ResearchComponent = ({
  auth,
  warningPopupClicked,
  updatePopupClicked,
  officesOptions,
  companiesOptions,
  internalLevelsOptions,
  languagesOptions,
  managerOptions,
  roleOptions,
  sectorOptions,
  softSkillsOptions,
  technicalSkillsOptions,
  certificationsOptions,
  categoriesOptions,
  customerOptions,
  researchRaw,
  loading,
  getResearch,
  createDocs,
  language,
  reloadSoftSkills,
  getTechnicalSkills,
  getLanguages,
}) => {
  const [filtersVisible, setFiltersVisible] = useState(false)
  const [searchValue, setSearchValue] = useState("")
  const [filtersId, setFiltersId] = useState(INITIAL_FILTERS)
  const [categoryId, setCategoryId] = useState("")
  const [categoryName, setCategoryName] = useState("")
  const [selectedCategory, setSelectedCategory] = useState(false)
  const [userId, setUserId] = useState(0)
  const [uniqueId, setUniqueId] = useState(null)
  const [downloadCvClicked, setDownloadCvClicked] = useState(false)
  const [openModalCV, setOpenModalCV] = useState(false)

  // Ref to hold the latest filtersId for use inside the debounced callback
  const filtersRef = useRef(filtersId)
  filtersRef.current = filtersId

  // Debounced API call — fires once after the user stops typing
  const debouncedSearch = useCallback(
    _.debounce((value) => {
      if (value.length > 2) {
        getResearch({ ...filtersRef.current, nameSurname: value })
      }
    }, DEBOUNCE_DELAY),
    [getResearch]
  )

  // Cleanup debounce on unmount
  useEffect(() => {
    return () => {
      debouncedSearch.cancel()
    }
  }, [debouncedSearch])

  useEffect(() => {
    if (amISuperAdmin(auth.abilities) && !warningPopupClicked) {
      updatePopupClicked()
    }
  }, [auth.abilities, warningPopupClicked, updatePopupClicked])

  const researchCols = [
    {
      Header: "Name",
      accessor: "name",
    },
    {
      Header: "Surname",
      accessor: "surname",
    },
    {
      Header: "CV",
      Cell: () => (
        <div className="research-cv-cell">
          <Button onClick={handleOpenModalCV}>Download CV</Button>
        </div>
      ),
    },
  ]

  const handleSearch = (e, { value }) => {
    setSearchValue(value)
    const updatedFilters = { ...filtersId, nameSurname: value }
    setFiltersId(updatedFilters)

    if (value.length > 2) {
      debouncedSearch(value)
    } else if (value.length === 0) {
      // Reset results when input is cleared
      debouncedSearch.cancel()
      getResearch({ ...updatedFilters, nameSurname: "" })
    }
  }

  const handleSearchKeyDown = (e) => {
    if (e.key === "Enter" && searchValue.length > 2) {
      debouncedSearch.cancel()
      getResearch({ ...filtersId, nameSurname: searchValue })
    }
  }

  const searchCV = () => {
    getResearch(filtersId)
  }

  const toggleFiltersVisibility = () => {
    setFiltersVisible((prev) => !prev)
    reloadSoftSkills()
    getTechnicalSkills(categoryId)
    getLanguages(language)
  }

  const onSelectFilterChange = (e, data) => {
    const name = data.name
    const valueList = data.value

    if (name === "office") {
      setFiltersId((prev) => ({ ...prev, [name]: data.value }))
    } else if (name === "practice" || name === "manager") {
      const value = []
      valueList.forEach((v) => {
        data.options.forEach((o) => {
          if (o.value === v) {
            value.push(o.code || o.value)
          }
        })
      })
      setFiltersId((prev) => ({ ...prev, [name]: value }))
    } else {
      const value = []
      valueList.forEach((v) => {
        data.options.forEach((o) => {
          if (o.value === v) {
            value.push(o.key)
          }
        })
      })
      setFiltersId((prev) => ({ ...prev, [name]: value }))
    }
  }

  const resetFilters = () => {
    setFiltersId(INITIAL_FILTERS)
    setCategoryId("")
    setCategoryName("")
    setSelectedCategory(false)
  }

  const removeFilter = (filterName) => {
    const updated = { ...filtersId, [filterName]: null }
    setFiltersId(updated)

    if (filterName === "technicalSkill") {
      setCategoryId("")
      setCategoryName("")
      setSelectedCategory(false)
    }
  }

  const selectCategory = (e, data) => {
    const name = data.value
    data.options.forEach((opt) => {
      if (opt.value === name) {
        setCategoryId(opt.key)
        setSelectedCategory(true)
        setCategoryName(opt.value)
        getTechnicalSkills(opt.key)
      }
    })
  }

  const onSelectRow = (clickedRow) => {
    if (downloadCvClicked) return

    const found = (researchRaw || []).find((row) => row.id === clickedRow.id)
    if (found) {
      setUserId(clickedRow.id)
      setUniqueId(clickedRow.unique_id)
      const str =
        clickedRow.unique_id +
        "-" +
        clickedRow.unique_id +
        "-" +
        clickedRow.name +
        "-" +
        clickedRow.surname
      history.push("/cvGenerator/" + str)
    }
  }

  const handleOpenModalCV = () => {
    setOpenModalCV(true)
    setDownloadCvClicked(true)
  }

  const handleCloseModalCV = () => {
    setOpenModalCV(false)
    setDownloadCvClicked(false)
  }

  const downloadDocsCv = () => {
    const onFailure = (res) => {
      let description = ""
      if (res.response && res.response.status === 406) {
        description = res.response.data.error
      } else if (res.response) {
        description = "Error creating CV document. Try Again"
      } else {
        description = "You cannot perform this action."
      }
      toast({
        title: "CV Docs",
        description: description,
        type: "error",
        icon: "file",
        time: constants.TOAST_ERROR_TIMEOUT,
        animation: "fly left",
      })
    }

    const onSuccess = () => {
      toast({
        title: "CV Docs",
        description: "Successfully created.",
        type: "success",
        icon: "check",
        time: constants.TOAST_SUCCESS_TIMEOUT,
        animation: "fly left",
      })
    }

    createDocs(uniqueId, language, onSuccess, onFailure)
    handleCloseModalCV()
  }

  /**
   * Returns the list of active filters as { name, label, values } objects
   * for rendering as chips.
   */
  const getActiveFilters = () => {
    const active = []
    Object.keys(FILTER_LABELS).forEach((key) => {
      const value = filtersId[key]
      if (value && ((Array.isArray(value) && value.length > 0) || (!Array.isArray(value) && value !== ""))) {
        active.push({
          name: key,
          label: FILTER_LABELS[key],
          value: Array.isArray(value) ? value.length + " selected" : value,
        })
      }
    })
    return active
  }

  const hasActiveFilters = getActiveFilters().length > 0

  const data = researchRaw || []

  return (
    <div className="research-container">
      <Label className="label-icon research-search-label">Search</Label>
      <Popup
        content="Enter consultant Name and Surname..."
        trigger={
          <Label color="teal" circular>
            ?
          </Label>
        }
      />

      <Grid columns={2} stackable>
        <Grid.Row>
          <Grid.Column width={8}>
            <Search
              input={{ fluid: true, onKeyDown: handleSearchKeyDown }}
              onSearchChange={handleSearch}
              placeholder="Enter Name and Surname..."
              showNoResults={false}
              minCharacters={3}
              loading={loading}
              value={searchValue}
              name="nameSurname"
            />
          </Grid.Column>
          <Grid.Column width={8}>
            <div className="research-filter-controls">
              <Button
                basic
                content={filtersVisible ? "Hide Filters" : "Filters"}
                onClick={toggleFiltersVisibility}
                color="teal"
                icon="filter"
              />
              {hasActiveFilters && (
                <Button
                  basic
                  content="Reset filters"
                  onClick={resetFilters}
                  color="teal"
                  icon="undo"
                />
              )}
            </div>
          </Grid.Column>
        </Grid.Row>
      </Grid>

      {/* Active filters chips — visible even when panel is closed */}
      {!filtersVisible && hasActiveFilters && (
        <div className="research-active-filters">
          {getActiveFilters().map((filter) => (
            <Label key={filter.name} color="teal" size="small">
              {filter.label}: {filter.value}
              <Icon name="delete" onClick={() => removeFilter(filter.name)} />
            </Label>
          ))}
        </div>
      )}

      {filtersVisible && (
        <Segment clearing secondary className="research-filters-panel">
          <div className="research-filters-content">
            {/* Row 1: Competencies */}
            <Grid stackable className="research-filters-row">
              <Grid.Row columns={4}>
                <Grid.Column>
                  <Form.Field>
                    <label>Certification</label>
                    <Dropdown
                      placeholder="Select..."
                      multiple
                      search
                      clearable
                      selection
                      fluid
                      options={certificationsOptions}
                      onChange={onSelectFilterChange}
                      name="certification"
                    />
                  </Form.Field>
                </Grid.Column>
                <Grid.Column>
                  <Form.Field>
                    <label>Business Title</label>
                    <Dropdown
                      placeholder="Select..."
                      multiple
                      search
                      clearable
                      selection
                      fluid
                      options={roleOptions}
                      onChange={onSelectFilterChange}
                      name="role"
                    />
                  </Form.Field>
                </Grid.Column>
                <Grid.Column>
                  <Form.Field>
                    <label>Sector</label>
                    <Dropdown
                      placeholder="Select..."
                      multiple
                      search
                      clearable
                      selection
                      fluid
                      options={sectorOptions}
                      onChange={onSelectFilterChange}
                      name="sector"
                    />
                  </Form.Field>
                </Grid.Column>
                <Grid.Column>
                  <Form.Field>
                    <label>Soft Skill</label>
                    <Dropdown
                      placeholder="Select..."
                      name="softSkill"
                      multiple
                      search
                      clearable
                      selection
                      fluid
                      options={softSkillsOptions}
                      onChange={onSelectFilterChange}
                    />
                  </Form.Field>
                </Grid.Column>
              </Grid.Row>
            </Grid>

            {/* Row 2: Technical Skills */}
            <Grid stackable className="research-filters-row">
              <Grid.Row columns={4}>
                <Grid.Column>
                  <Form.Field>
                    <label>Category of Technical Skill</label>
                    <Dropdown
                      placeholder="Select..."
                      search
                      selection
                      fluid
                      multiple={false}
                      options={categoriesOptions}
                      onChange={selectCategory}
                      name="name"
                      value={categoryName}
                    />
                  </Form.Field>
                </Grid.Column>
                {selectedCategory && (
                  <Grid.Column>
                    <Form.Field>
                      <label>Technical Skill</label>
                      <Dropdown
                        placeholder="Select..."
                        search
                        clearable
                        selection
                        multiple
                        fluid
                        options={technicalSkillsOptions}
                        onChange={onSelectFilterChange}
                        name="technicalSkill"
                      />
                    </Form.Field>
                  </Grid.Column>
                )}
              </Grid.Row>
            </Grid>

            {/* Row 3: Organization */}
            <Grid stackable className="research-filters-row">
              <Grid.Row columns={4}>
                <Grid.Column>
                  <Form.Field>
                    <label>Internal Level</label>
                    <Dropdown
                      placeholder="Select..."
                      name="internalLevel"
                      multiple
                      search
                      clearable
                      selection
                      fluid
                      options={internalLevelsOptions}
                      onChange={onSelectFilterChange}
                    />
                  </Form.Field>
                </Grid.Column>
                <Grid.Column>
                  <Form.Field>
                    <label>Office Location</label>
                    <Dropdown
                      placeholder="Select..."
                      name="office"
                      search
                      clearable
                      selection
                      fluid
                      options={officesOptions}
                      onChange={onSelectFilterChange}
                    />
                  </Form.Field>
                </Grid.Column>
                <Grid.Column>
                  <Form.Field>
                    <label>Company</label>
                    <Dropdown
                      placeholder="Select..."
                      name="company"
                      multiple
                      search
                      clearable
                      selection
                      fluid
                      options={companiesOptions}
                      onChange={onSelectFilterChange}
                    />
                  </Form.Field>
                </Grid.Column>
              </Grid.Row>
            </Grid>

            {/* Row 4: Relations */}
            <Grid stackable className="research-filters-row">
              <Grid.Row columns={4}>
                <Grid.Column>
                  <Form.Field>
                    <label>Manager</label>
                    <Dropdown
                      placeholder="Select..."
                      name="manager"
                      multiple
                      search
                      clearable
                      selection
                      fluid
                      options={managerOptions}
                      onChange={onSelectFilterChange}
                    />
                  </Form.Field>
                </Grid.Column>
                <Grid.Column>
                  <Form.Field>
                    <label>Customer</label>
                    <Dropdown
                      placeholder="Select..."
                      name="customer"
                      multiple
                      search
                      clearable
                      selection
                      fluid
                      options={customerOptions}
                      onChange={onSelectFilterChange}
                    />
                  </Form.Field>
                </Grid.Column>
                <Grid.Column>
                  <Form.Field>
                    <label>Language</label>
                    <Dropdown
                      placeholder="Select..."
                      name="language"
                      multiple
                      search
                      clearable
                      selection
                      fluid
                      options={languagesOptions}
                      onChange={onSelectFilterChange}
                    />
                  </Form.Field>
                </Grid.Column>
              </Grid.Row>
            </Grid>

            <Divider />

            <div className="research-filters-actions">
              <IconButton
                icon="search"
                label="Search"
                onClick={searchCV}
                loading={loading}
              />
            </div>
          </div>
        </Segment>
      )}

      <Segment clearing className="research-results-segment">
        <Header>Search Results</Header>
        <div className="research-results-content">
          <PaginatedReusableTable
            columns={researchCols}
            data={data}
            loading={loading}
            pageSize={5}
            onClick={onSelectRow}
          />
        </div>
      </Segment>

      <Modal
        dimmer="blurring"
        open={openModalCV}
        onClose={handleCloseModalCV}
        size="mini"
        closeIcon
      >
        <Modal.Header>Download CV</Modal.Header>
        <Modal.Actions>
          <Button onClick={downloadDocsCv}>Download</Button>
        </Modal.Actions>
      </Modal>
    </div>
  )
}

ResearchComponent.propTypes = {
  auth: PropTypes.object.isRequired,
  warningPopupClicked: PropTypes.bool.isRequired,
  updatePopupClicked: PropTypes.func.isRequired,
  officesOptions: PropTypes.array,
  companiesOptions: PropTypes.array,
  internalLevelsOptions: PropTypes.array,
  languagesOptions: PropTypes.array,
  managerOptions: PropTypes.array,
  roleOptions: PropTypes.array,
  sectorOptions: PropTypes.array,
  softSkillsOptions: PropTypes.array,
  technicalSkillsOptions: PropTypes.array,
  certificationsOptions: PropTypes.array,
  categoriesOptions: PropTypes.array,
  customerOptions: PropTypes.array,
  researchRaw: PropTypes.array,
  loading: PropTypes.bool,
  getResearch: PropTypes.func.isRequired,
  createDocs: PropTypes.func.isRequired,
  language: PropTypes.string,
  reloadSoftSkills: PropTypes.func.isRequired,
  getTechnicalSkills: PropTypes.func.isRequired,
  getLanguages: PropTypes.func.isRequired,
}

export default ResearchComponent
